export const NO_FAILURE_DETAIL = Object.freeze({
    memoryConflict: null,
    invalidField: null,
    tripRefusal: null,
    requestConflict: null,
    quoteConflict: null,
    itineraryRefusal: null,
    teamConflict: null,
    handoffConflict: null,
    payment: null,
    importRefusal: null,
});
/** The API answered with an error status. Every client family extends it and adds the refusal data of its own area. */
export class ApiHttpError extends Error {
    status;
    code;
    requestId;
    /** Seconds from the `Retry-After` header, or `null`. */
    retryAfter;
    constructor(status, code, message, requestId, retryAfter = null) {
        super(message);
        this.name = "ApiHttpError";
        this.status = status;
        this.code = code;
        this.requestId = requestId;
        this.retryAfter = retryAfter;
    }
    /** The area's own conflict code, or `null`. */
    get conflictCode() {
        return null;
    }
    /** `true` for a stale write: the thing changed since the client read it. */
    get staleView() {
        return false;
    }
    /** The typed refusal data of the body. */
    get detail() {
        return NO_FAILURE_DETAIL;
    }
}
/** The request never got an answer (no network, DNS, a closed connection) after every retry. */
export class ApiNetworkError extends Error {
    constructor(message, options) {
        super(message, options);
        this.name = "ApiNetworkError";
    }
}
/** The API answered 2xx with a body that does not match the contract. */
export class ApiResponseError extends Error {
    constructor(message) {
        super(message);
        this.name = "ApiResponseError";
    }
}
/** The storage refused or could not take a file. `status` is the storage's HTTP status, or 0 when it never answered. */
export class ApiUploadError extends Error {
    status;
    constructor(status, message = "the file could not be uploaded", options) {
        super(message);
        this.name = "ApiUploadError";
        this.status = status;
        if (options?.cause !== undefined)
            this.cause = options.cause;
    }
}
/** The kind of an HTTP status. One table for the whole package. */
export function failureKindOfStatus(status) {
    switch (status) {
        case 401:
            return "unauthenticated";
        case 403:
            return "forbidden";
        case 404:
            return "not_found";
        case 409:
            return "conflict";
        case 422:
            return "invalid";
        case 400:
            return "bad_request";
        case 413:
            return "too_large";
        case 429:
            return "rate_limited";
        case 408:
            return "timeout";
        case 503:
            return "unavailable";
        case 500:
        case 502:
        case 504:
            return "server";
        default:
            return "other_status";
    }
}
const RETRYABLE_KINDS = new Set([
    "network",
    "upload",
    "rate_limited",
    "unavailable",
    "server",
]);
const made = (kind, fields = {}) => ({
    kind,
    status: null,
    code: null,
    requestId: null,
    retryAfter: null,
    retryable: RETRYABLE_KINDS.has(kind),
    reload: false,
    staleView: false,
    inFlight: false,
    conflictCode: null,
    detail: NO_FAILURE_DETAIL,
    ...fields,
});
/**
 * The one reading of a thrown value. Any error of any client of this package gives an `ApiFailure`. A value that is none of the
 * families (a bug, an abort, an error of a hook) is `kind: "other"`: the caller words it as "something went wrong".
 */
export function classifyFailure(error) {
    if (error instanceof ApiHttpError) {
        const kind = failureKindOfStatus(error.status);
        const conflictCode = error.conflictCode;
        return made(kind, {
            status: error.status,
            code: error.code,
            requestId: error.requestId,
            retryAfter: error.retryAfter,
            reload: kind === "conflict",
            staleView: error.staleView,
            inFlight: kind === "conflict" && conflictCode === null && error.retryAfter !== null,
            conflictCode,
            detail: error.detail,
        });
    }
    if (error instanceof ApiNetworkError)
        return made("network");
    if (error instanceof ApiResponseError)
        return made("response");
    if (error instanceof ApiUploadError)
        return made("upload", { status: error.status });
    return made("other");
}
